import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskLines, Reveal } from "../components/Reveal";
import { ArrowButton } from "../components/Buttons";
import { getService, SERVICES } from "../data/site";
import { Check, ArrowRight } from "lucide-react";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div data-testid={`service-page-${slug}`}>
      {/* Hero */}
      <section className="bg-bottle-green text-cream pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="flex items-center gap-4 label text-cream/55"
          >
            <span className="font-mono">{service.num}</span>
            <span className="w-8 h-px bg-cream/40" />
            <span>{service.fullTitle || service.name}</span>
          </motion.div>

          <h1 className="mt-7">
            <MaskLines
              lines={service.headline}
              delay={0.2}
              lineClass="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-cream"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
            className="font-sans text-lg md:text-xl text-cream/75 leading-relaxed max-w-2xl mt-10"
          >
            {service.intro}
          </motion.p>
        </div>
      </section>

      {/* Lead image */}
      <section className="bg-cream">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 -mt-2">
          <Reveal className="overflow-hidden rounded-sm aspect-[16/9] md:aspect-[21/9] bg-warm-beige -translate-y-10 md:-translate-y-14 shadow-2xl shadow-bottle-green/20">
            <img src={service.images[0]} alt={service.name} className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Flow (e-commerce) */}
      {service.flow && (
        <section className="bg-cream pb-8">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <Reveal className="flex flex-wrap items-center gap-x-4 gap-y-3 py-8 border-y border-dark-text/10">
              {service.flow.map((f, i) => (
                <div key={f} className="flex items-center gap-4">
                  <span className="font-serif text-2xl md:text-4xl tracking-tight text-bottle-green">{f}</span>
                  {i < service.flow.length - 1 && <ArrowRight className="w-5 h-5 text-dark-text/40" strokeWidth={1.4} />}
                </div>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {/* Capabilities */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="label text-dark-text/50">What's included</span>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-dark-text mt-5 leading-tight">
                Capabilities & deliverables
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <div className="grid sm:grid-cols-2 gap-px bg-dark-text/10 border border-dark-text/10">
              {service.capabilities.map((c, i) => (
                <Reveal key={c} delay={(i % 2) * 0.05} className="bg-cream p-6 flex items-center gap-4 group hover:bg-warm-beige transition-colors duration-300">
                  <span className="w-8 h-8 shrink-0 rounded-full border border-bottle-green/30 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-bottle-green" strokeWidth={2} />
                  </span>
                  <span className="font-sans text-base text-dark-text">{c}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {service.images.length > 1 && (
        <section className="bg-warm-beige py-20 md:py-28">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <Reveal><span className="label text-dark-text/50">{service.grid ? "Campaign grid" : "In the work"}</span></Reveal>
            <div className={`mt-8 grid gap-4 md:gap-6 ${service.images.length >= 4 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {service.images.slice(1).map((img, i) => (
                <Reveal key={i} delay={i * 0.08} className={`overflow-hidden rounded-sm bg-cream ${service.images.length >= 4 && i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                  <div className={`overflow-hidden ${service.images.length >= 4 && i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[4/3]"}`}>
                    <img src={img} alt={`${service.name} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA + next */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-dark-text leading-tight">
              Ready to explore <span className="italic">{service.name}</span>?
            </h2>
            <div className="flex flex-wrap gap-4 mt-8">
              <ArrowButton to="/contact" testid="service-start-project">Start a Project</ArrowButton>
              <ArrowButton to="/services" variant="outline" testid="service-all-services">All Services</ArrowButton>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:text-right">
            <span className="label text-dark-text/45">Next service</span>
            <Link to={`/services/${next.slug}`} data-testid="service-next" className="group block mt-4">
              <span className="font-serif text-3xl md:text-4xl tracking-tight text-dark-text group-hover:text-secondary-green transition-colors">
                {next.name}
              </span>
              <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase text-bottle-green md:justify-end">
                <span className="link-underline">Explore Service</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
